/**
 * The particle budget, and the motion/visibility policy.
 *
 * Compiled into BOTH surfaces. No DOM writes here — this module only decides
 * numbers and answers questions.
 *
 * Three merchant complaints in the research corpus are performance complaints
 * ("slowed my shop to 4s page loads", "slowing my store down so much I had
 * customer complaints", "slows my laptop down extremely"), and only 7 of 180
 * positive reviews volunteer that speed is fine — merchants raise it only when
 * they were already worried. So the budget is scaled to the device rather than
 * being a constant a merchant can wind up until their phone melts.
 */

export interface DeviceProfile {
  cores: number;
  memoryGb: number;
  /** devicePixelRatio, clamped — a 3x phone does not need a 3x atlas. */
  dpr: number;
  coarsePointer: boolean;
  /** Viewport width in CSS px. */
  width: number;
}

export function readDevice(win: Window = window): DeviceProfile {
  const nav = win.navigator as Navigator & { deviceMemory?: number };
  return {
    cores: Math.max(1, nav.hardwareConcurrency || 4),
    memoryGb: Math.max(0.5, nav.deviceMemory || 4),
    dpr: Math.min(2, win.devicePixelRatio || 1),
    coarsePointer: win.matchMedia?.('(pointer: coarse)').matches ?? false,
    width: win.innerWidth || 1280,
  };
}

/**
 * Particles actually spawned, given the merchant's density and the device.
 *
 * The ceiling matters more than the formula: a merchant who drags density to 2.0
 * on a preset built for desktop must not ship 600 sprites to a 4-core phone.
 */
export function particleBudget(base: number, density: number, dev: DeviceProfile): number {
  const wanted = base * clamp(density, 0.25, 2);

  // Cores and memory each contribute a multiplier; the weaker one dominates.
  const coreFactor = clamp(dev.cores / 8, 0.35, 1);
  const memFactor = clamp(dev.memoryGb / 8, 0.35, 1);
  const deviceFactor = Math.min(coreFactor, memFactor);

  // Narrow viewports get proportionally fewer — the same count on a 375px screen
  // is visually four times denser and costs the same to draw.
  const widthFactor = clamp(dev.width / 1280, 0.4, 1);

  const ceiling = dev.coarsePointer ? 140 : 420;
  return Math.max(8, Math.min(ceiling, Math.round(wanted * deviceFactor * widthFactor)));
}

/**
 * Atlas cell size in device px. One cell holds the sprite at its LARGEST
 * on-screen size; everything smaller is a downscaled blit, which is free.
 */
export function atlasCellPx(maxSizeCss: number, dev: DeviceProfile): number {
  return Math.ceil(clamp(maxSizeCss, 8, 128) * dev.dpr);
}

/**
 * `prefers-reduced-motion`, with a LIVE subscription.
 *
 * Reading it once at boot is the common mistake: a shopper who enables the OS
 * setting mid-session keeps the animation. WCAG 2.2.2 applies to auto-starting
 * motion over five seconds, which is every effect this app ships.
 *
 * Returns an unsubscribe function.
 */
export function watchReducedMotion(
  onChange: (reduced: boolean) => void,
  win: Window = window
): () => void {
  const mq = win.matchMedia?.('(prefers-reduced-motion: reduce)');
  if (!mq) {
    onChange(false);
    return () => {};
  }
  const handler = () => onChange(mq.matches);
  handler();
  mq.addEventListener('change', handler);
  return () => mq.removeEventListener('change', handler);
}

/**
 * Pause when the tab is hidden.
 *
 * Not only politeness: a paused rAF loop in a background tab is throttled to ~1 Hz
 * by the browser anyway, so an unpaused loop wakes the CPU for nothing and drains
 * a phone battery the merchant will blame the store for.
 */
export function watchVisibility(onChange: (visible: boolean) => void, doc: Document = document) {
  const handler = () => onChange(!doc.hidden);
  handler();
  doc.addEventListener('visibilitychange', handler);
  return () => doc.removeEventListener('visibilitychange', handler);
}

/**
 * WCAG 2.3.1 — nothing may flash more than three times per second.
 *
 * This is the rule nobody in the category has thought about and it points
 * straight at fireworks, sparkle, twinkle, strobe and lightning. It is enforced
 * here as a hard clamp on any oscillator frequency a family asks for, so a
 * mis-tuned preset cannot ship a seizure risk. A family that WANTS to flash
 * faster is a rebuild, not a tuning pass.
 */
export const MAX_FLASH_HZ = 3;

export function clampFlashHz(hz: number): number {
  return Math.min(MAX_FLASH_HZ, Math.max(0, hz));
}

export function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

/**
 * Deterministic PRNG (mulberry32).
 *
 * Deterministic on purpose: the admin preview and a screenshot capture must be
 * able to reproduce a field exactly, and `Math.random()` makes a gallery frame
 * unreproducible.
 */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
